/**
 * `AgentColumn` — the left column (desktop): phase narration list +
 * tier-gated `MandateSigningCard`. The picker + Reset + Run header
 * moved to the shared sticky `SimulationControls` strip (ADR-0163 §4)
 * so mobile and desktop share one control locus; the internal scroll
 * cage is gone — the page scrolls (ADR-0163 §6).
 */

import { motion } from 'framer-motion';

import { Card } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import type { Phase } from '../journeys/types.js';
import type { AgentDef, VerificationTier } from '../roster.js';
import { MandateSigningCard, mandateKindFor } from './MandateSigningCard.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/**
 * Collapse CONSECUTIVE skipped phases into one display row (the blocked
 * journey skips four in a row — four identical "Skipped" rows read as
 * filler; 2026-07-06 consolidation). The merged row carries the joined
 * kicker (`INTENT · CART · PAYMENT · FULFILLMENT`).
 */
export function collapseSkipped(phases: Phase[]): Phase[] {
  const out: Phase[] = [];
  for (const p of phases) {
    const prev = out[out.length - 1];
    if (p.skipped === true && prev !== undefined && prev.skipped === true) {
      out[out.length - 1] = { ...prev, name: `${prev.name} · ${p.name}` };
      continue;
    }
    out.push(p);
  }
  return out;
}

export interface AgentColumnProps {
  agent: AgentDef;
  phases: Phase[];
  step: number;
  /** Registry-backed tier for the CURRENTLY-selected agent. */
  tier: VerificationTier;
}

export function AgentColumn({ agent, phases, step, tier }: AgentColumnProps): JSX.Element {
  const C = useC();
  const visiblePhases = collapseSkipped(phases.slice(0, step + 1));

  return (
    <Card className="p-5">
      <div className="space-y-3">
        {visiblePhases.map((p, i) => {
          const mandate = mandateKindFor(p.id);
          const isLatest = i === visiblePhases.length - 1;
          return (
            <motion.div key={p.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className="flex gap-3">
              <div className="flex-shrink-0" style={{ width: 4, background: p.skipped ? C.dim : p.ok ? C.limeFg : C.coralFg, borderRadius: 1 }} />
              <div className="flex-1 min-w-0">
                <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1, marginBottom: 4 }}>{p.name.toUpperCase()}</div>
                {p.skipped ? (
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim, fontStyle: 'italic' }}>Skipped — the agent didn't get past the identity check.</div>
                ) : (
                  <>
                    <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: mandate ? 8 : 0 }}>{p.narration}</div>
                    {mandate && <MandateSigningCard mandate={mandate} agent={agent} animate={isLatest} tier={tier} />}
                  </>
                )}
              </div>
            </motion.div>
          );
        })}
        {step < 0 && (
          <div className="text-center py-12" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            Press <span style={{ color: C.text }}>Run</span> to start.
          </div>
        )}
      </div>
    </Card>
  );
}
